// Generated by GitHub Copilot
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import styles from './GCCHealthCheck.module.css';
import { GCC_HEALTH_CHECK_POC_EMAIL } from '../../constants/contacts';

const STATS = [
  {
    id: 'global',
    value: '>8,000',
    label: 'GCCs worldwide, with India firmly at the center of momentum.',
  },
  {
    id: 'india',
    value: '1,800',
    label: 'GCCs in India, supported by a deep and fast-scaling talent base.',
  },
  {
    id: 'partnerships',
    value: '2 in 3',
    label: 'GCCs expect provider partnerships to become core to strategy in the next 12–24 months.',
  },
];

const DELIVERABLES = [
  {
    id: 'benchmarking',
    number: '01',
    title: 'Maturity benchmarking',
    description:
      'Compares current-state maturity with future ambition across four levels: Foundational, Emerging, Advanced and Leading.',
  },
  {
    id: 'gap',
    number: '02',
    title: 'Gap analysis',
    description:
      'Pinpoints where the GCC is misaligned across five pillars and 16 dimensions, making gaps visible and actionable.',
  },
  {
    id: 'recommendations',
    number: '03',
    title: 'Priority recommendations',
    description:
      'Turns survey inputs into a concise report with focus areas, next-best actions and a clear path to accelerate progress.',
  },
  {
    id: 'investment',
    number: '04',
    title: 'Investment alignment',
    description:
      'Highlights where client investments are flowing today versus where capability gaps demand attention.',
  },
];

const PILLARS = [
  {
    id: 'vision',
    number: '1',
    title: 'Vision and strategy',
    description: 'Adoption, empowerment, ownership, decision making and ecosystem collaboration.',
  },
  {
    id: 'governance',
    number: '2',
    title: 'Structure and governance',
    description:
      'Business impact, performance philosophy, global capability ownership and technology integration.',
  },
  {
    id: 'talent',
    number: '3',
    title: 'Talent and culture',
    description: 'Future-ready workforce, skill development, culture and employee value proposition.',
  },
  {
    id: 'capability',
    number: '4',
    title: 'Capability depth',
    description:
      'Enterprise coverage, role depth, enabling capabilities, innovation, agility and business continuity.',
  },
  {
    id: 'innovation',
    number: '5',
    title: 'Innovation and transformation',
    description:
      'Reinvention focus, investment ownership, innovation maturity and AI-native operations.',
  },
];

const JOURNEY_STEPS = [
  {
    id: 'invite',
    time: 'Day 0',
    title: 'Invite the client',
    description: 'CALs introduce the diagnostic, create interest and secure participation.',
  },
  {
    id: 'survey',
    time: 'Week 1',
    title: 'Survey and report',
    description: 'Clients complete the survey and receive a personalized maturity report.',
  },
  {
    id: 'review',
    time: 'Week 2/3',
    title: 'Review and activate',
    description:
      'Teams discuss findings, align implications and identify deep-dive opportunities.',
  },
];

const DIAGNOSTIC_URL =
  'https://ts.accenture.com/:x:/r/sites/GCCOffering/Shared%20Documents/General/10.%20GCC%20Solution/02.%20Solution%20Narrative/Capability%20Stage%200/GCC%20Run%20%26%20Evolve/GCC%20Health%20Check/GCC%20Maturity%20Diagnostic%20Survey%20Participants.xlsx?d=wb0d9a60953394454aa356a929f2100db&csf=1&web=1&e=fVthus';

const GCCHealthCheck = () => {
  const navigate = useNavigate();

  return (
    <div className={styles.page}>
      <Navbar />
      <div className={styles.heroPadding} />

      {/* ── Back button ── */}
      <div className={styles.backBar}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={styles.backButton}
          aria-label="Go back to previous page"
        >
          <svg className={styles.backIcon} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      {/* ── Hero ── */}
      <section className={styles.heroSection} aria-labelledby="gcc-hc-heading">
        <div aria-hidden="true" className={styles.bokehContainer}>
          <div className={styles.blobLeft} />
          <div className={styles.blobCenter} />
          <div className={styles.blobRight} />
        </div>

        <div className={styles.heroInner}>
          <div className={styles.heroLeft}>
            <p className={styles.eyebrow}>Assess. Identify. Accelerate</p>
            <h1 id="gcc-hc-heading" className={styles.heroHeading}>
              GCC Maturity Diagnostic
            </h1>
            <p className={styles.heroSubtext}>
              A focused, survey-led diagnostic that benchmarks a GCC’s current maturity against
              future ambition, identifies the highest-value gaps, and translates them into a
              practical action agenda for enterprise reinvention.
            </p>
            <div className={styles.heroActions}>
              {/* <a
                href={DIAGNOSTIC_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.btnPrimary}
                aria-label="Open GCC Maturity Diagnostic (opens in a new tab)"
              >
                GCC Maturity Diagnostic &nbsp;&rsaquo;
              </a> */}
              <a href="#how" className={styles.btnGhost}>
                See how it works &nbsp;&rsaquo;
              </a>
            </div>
          </div>

          <aside className={styles.statsPanel} aria-label="Market context">
            <p className={styles.statsPanelLabel}>Why it matters now</p>
            <div className={styles.statsGrid}>
              {STATS.map((s) => (
                <div key={s.id} className={styles.statCard}>
                  <strong className={styles.statValue}>{s.value}</strong>
                  <span className={styles.statLabel}>{s.label}</span>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>

      {/* ── What the diagnostic delivers ── */}
      <section className={styles.contentSection} aria-labelledby="delivers-heading">
        <div className={styles.sectionHeader}>
          <h2 id="delivers-heading" className={styles.sectionHeading}>
            What the diagnostic delivers
          </h2>
        </div>

        <div className={styles.deliverablesGrid}>
          {DELIVERABLES.map((d) => (
            <article key={d.id} className={styles.card}>
              <p className={styles.cardNumber}>{d.number}</p>
              <h3 className={styles.cardTitle}>{d.title}</h3>
              <p className={styles.cardDesc}>{d.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── Five-pillar framework ── */}
      <section className={styles.contentSection} aria-labelledby="framework-heading">
        <div className={styles.sectionHeader}>
          <h2 id="framework-heading" className={styles.sectionHeading}>
            Five-pillar view of GCC maturity
          </h2>
        
        </div>

        <div className={styles.pillarsGrid}>
          {PILLARS.map((p) => (
            <article key={p.id} className={styles.pillar}>
              <span className={styles.pillarNumber} aria-hidden="true">{p.number}</span>
              <h3 className={styles.pillarTitle}>{p.title}</h3>
              <p className={styles.pillarDesc}>{p.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── Client journey + CTA ── */}
      <section id="how" className={styles.journeySection} aria-labelledby="journey-heading">
        <div className={styles.journeyWrap}>
          <div className={styles.journeyLeft}>
            <h2 id="journey-heading" className={styles.sectionHeading}>
              A compact client journey
            </h2>
            <p className={styles.sectionLead}>
              The experience is intentionally lightweight: introduce the diagnostic, complete
              the survey, receive an AI-generated report, then review implications and next
              steps with Accenture teams.
            </p>

            <div className={styles.stepsGrid}>
              {JOURNEY_STEPS.map((step) => (
                <article key={step.id} className={styles.step}>
                  <span className={styles.stepTime}>{step.time}</span>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDesc}>{step.description}</p>
                </article>
              ))}
            </div>
          </div>

          <aside className={styles.ctaPanel} aria-label="Call to action">
            <div>
              <p className={styles.ctaPanelEyebrow}>Client Ready Asset</p>
              <h2 className={styles.ctaPanelHeading}>
                Start the maturity conversation
              </h2>
              <p className={styles.ctaPanelText}>
                Connect with the GCC Health Check POC to get started. They will help coordinate the required stakeholder inputs and work with the GCC Maturity Diagnostic Team to generate personalized survey links for each participant. Survey links will be shared directly with stakeholders on their registered email IDs.
              </p>
            </div>
            <a
              href={`mailto:${GCC_HEALTH_CHECK_POC_EMAIL}`}
              className={styles.btnSecondary}
              aria-label="Email the GCC Health Check POC"
            >
              GCC Health Check POC
            </a>
          </aside>
        </div>

        {/* ── Downloads ── */}
        <div className={styles.downloadsStrip}>
          <div className={styles.downloadsHeader}>
            <p className={styles.downloadsLabel}>GCC Maturity Assets</p>
           
          </div>
          <p className={styles.downloadsDesc}>
            Explore the framework behind the diagnostic and see what clients receive at the end of it
          </p>
          <div className={styles.downloadsRow}>
            <a
              href="/Input/Worksheet in GCC Studio changes.xlsx"
              download
              className={styles.btnDownloadPrimary}
              aria-label="Download the survey questionnaire (xlsx)"
            >
              Download the survey questionnaire
            </a>
            <a
              href="/Input/GCC Maturity Assessment Report.pptx"
              download
              className={styles.btnDownloadPrimary}
              aria-label="Download the sample maturity report (pptx)"
            >
              Download the sample maturity report
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GCCHealthCheck;
